import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { environment } from '../../../environments/environment'
import { Observable, concatMap } from 'rxjs'
import { dataCyclesReducer } from '@containers/data-cycles/data-cycles.reducers'

@Injectable()
export class BusinessesService {
  constructor(private http: HttpClient) {}

  public fetchBusinesses(params: any): Observable<any> {
    const { status, ...restParams } = params
    const endpoint = status === 'archived' ? 'archiveList' : 'list'
    const url = `${environment.API_ENDPOINT}/business/${endpoint}`
    return this.http.post(url, restParams)
  }

  public fetchBusiness(id: number): Observable<any> {
    const url = `${environment.API_ENDPOINT}/business/${id}`
    return this.http.get(url, {})
  }

  public updateBusiness(id: number, payload: object, options?: { validate?: boolean }): Observable<any> {
    let url = `${environment.API_ENDPOINT}/business/${id}`
    if (options?.validate) {
      url += '?validate=true'
    }
    return this.http.put(url, payload)
  }

  public createBusiness(payload: object): Observable<any> {
    const url = `${environment.API_ENDPOINT}/business`
    return this.http.post(url, payload)
  }

  public archiveBusiness(id: number): Observable<any> {
    const url = `${environment.API_ENDPOINT}/business/${id}/sendToArchive`
    return this.http.put(url, {})
  }

  public fetchBusinessDocuments(id: number): Observable<any> {
    const url = `${environment.API_ENDPOINT}/business/${id}/documents`
    return this.http.get(url, {})
  }

  public fetchBusinessPayments(id: number): Observable<any> {
    const url = `${environment.API_ENDPOINT}/business/${id}/financial`
    return this.http.get(url, {})
  }

  public downloadBusinessDocument({
    businessId,
    dataCycleId,
    id,
  }: any): Observable<any> {
    let url = `${environment.API_ENDPOINT}/business/${businessId}/downloadBL/${dataCycleId}`

    if (!dataCycleId) {
      url = `${environment.API_ENDPOINT}/document/businessDocument/${id}`
    }

    return this.http.get(url, {
      observe: 'response',
      responseType: 'blob' as 'json',
    })
  }

  public generateBusinessDocument({
    businessId,
    dataCycleId,
  }: any): Observable<any> {
    return this.http.get(
      `${environment.API_ENDPOINT}/business/${businessId}/generateBL`,
    )
  }

  public deleteBusinessDocument(businessId: string): Observable<any> {
    return this.http.get(
      `${environment.API_ENDPOINT}/business/${businessId}/deleteCurrentBL`,
    )
  }

  public createBusinessPayment({
    payableId,
    ...payload
  }: any): Observable<any> {
    const url = `${environment.API_ENDPOINT}/payment/${payableId}`
    return this.http
      .post(url, payload)
      .pipe(
        concatMap(() =>
          this.http.get(
            `${environment.API_ENDPOINT}/payment/payable/${payableId}`,
          ),
        ),
      )
  }

  public updateBusinessPayment({
    id,
    payableId,
    ...payload
  }: any): Observable<any> {
    const url = `${environment.API_ENDPOINT}/payment/${id}`
    return this.http
      .put(url, payload)
      .pipe(
        concatMap(() =>
          this.http.get(
            `${environment.API_ENDPOINT}/payment/payable/${payableId}`,
          ),
        ),
      )
  }

  public deleteBusinessPayment({ id, payable }: any): Observable<any> {
    const url = `${environment.API_ENDPOINT}/payment/${id}`

    return this.http
      .delete(url)
      .pipe(
        concatMap(() =>
          this.http.get(
            `${environment.API_ENDPOINT}/payment/payable/${payable.id}`,
          ),
        ),
      )
  }

  public downloadBusinessPayment(payment: any): Observable<any> {
    let url = `${environment.API_ENDPOINT}/payment/receipt/${payment.id}`

    return this.http.get(url, {
      observe: 'response',
      responseType: 'blob' as 'json',
    })
  }

  public uploadBusinessImage(id: number, payload: object): Observable<any> {
    const url = `${environment.API_ENDPOINT}/business/${id}/uploadImage`
    return this.http.post(url, payload)
  }

  public deleteBusinessImage(id: number, imageTypeId: number): Observable<any> {
    const url = `${environment.API_ENDPOINT}/business/${id}/deleteImage/${imageTypeId}`
    return this.http.delete(url)
  }

  public uploadDocument(id: any, payload: any): Observable<any> {
    const url = `${environment.API_ENDPOINT}/document/businessDocument/${id}`
    return this.http.put(url, payload)
  }

  public deleteDocument(id: any): Observable<any> {
    const url = `${environment.API_ENDPOINT}/document/businessDocument/${id}`
    return this.http.delete(url)
  }
}
